import { useCallback } from 'react'
import { useSWRConfig } from 'swr'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { TrackingApi } from '../api/session-logging-api'
import { CourseDataInterface } from '../types/course-interface'
import { CourseTrackingSessionCreateInterface } from '../types/course-lecture-interface'
import { CourseStepEnum } from '../types/course-step-type'
import { useCoursePathKey } from './use-course-data'

export const useTrackingApi = () => {
  const { mutate } = useSWRConfig()
  const courseDataKey = useCoursePathKey()

  // We can only receive 406 when course tracking settings
  // were updated during current session.
  // So, we need to invalidate the course data to get the latest settings.
  const lockedResponseHandler = useCallback(
    () =>
      mutate(
        courseDataKey,
        (data?: CourseDataInterface) => (data ? { ...data, lectureSessionLogging: false } : data),
        { revalidate: true },
      ),
    [courseDataKey, mutate],
  )

  const { fetcher: createFetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    CourseTrackingSessionCreateInterface
  >({
    method: RequestMethodsEnum.post,
    accessDeniedResponseHandler: () => null,
    lockedResponseHandler,
  })
  const { fetcher: updateFetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.post>({
    method: RequestMethodsEnum.post,
    lockedResponseHandler,
  })

  const startTrackingSession = useCallback((targetId: number, targetType: CourseStepEnum) => {
    switch (targetType) {
      case CourseStepEnum.lecture:
        return createFetcher(`${TrackingApi.InitLecture}/${targetId}/session/create`)
      case CourseStepEnum.quiz:
        return createFetcher(`${TrackingApi.InitQuiz}/${targetId}/session/create`)
    }
  }, [])

  const updateTrackingSession = useCallback((sessionId: number, targetType: CourseStepEnum) => {
    switch (targetType) {
      case CourseStepEnum.lecture:
        return updateFetcher(`${TrackingApi.UpdateLecture}/${sessionId}`)
      case CourseStepEnum.quiz:
        return updateFetcher(`${TrackingApi.UpdateQuiz}/${sessionId}`)
    }
  }, [])

  return { startTrackingSession, updateTrackingSession }
}
